/**
 * Size and opacity for whichever tool is active.
 *
 * The HUD had no way to change brush size at all — it was fixed at 24px, which
 * makes a hem and a fill the same stroke. That was the largest gap in the
 * editor and it had no obvious home, because the properties dock that would
 * have held it was replaced by Source.
 *
 * It lives in the rail, next to the tools it configures, rather than in a dock:
 * a dock is a place you go, and brush size is something you change constantly
 * without leaving the canvas. Same reasoning as the colour swatch beside it.
 *
 * **One set of settings, not one per tool.** The eraser shares the brush's size
 * and opacity by design — see tools.ts. Giving each its own copies Photoshop's
 * history rather than making a decision, and doubles the state for no benefit.
 */
import { attachPopover } from "./popover";
import type { Tools } from "./tools";

export interface ToolOptionsHandlers {
  /**
   * Where the panel first hangs from, and the fallback if it is opened without
   * one. `openAt` moves it — the panel belongs to whichever tool is active,
   * not to a fixed button.
   */
  anchor: HTMLElement;
  tools: Tools;
  onSize: (px: number) => void;
  onOpacity: (fraction: number) => void;
  onHardness: (hardness: number) => void;
  onFlow: (flow: number) => void;
  /**
   * Why a stroke would go nowhere right now, or null when it would land.
   *
   * `paintBlockedReason` has known this since the layer stack was written and
   * its own note says where it belongs: "Nothing surfaces this yet — the tool
   * options popover is where it goes." This is that.
   *
   * It matters most for the case that looks like a broken app rather than a
   * refusal: an image layer is selected, so the brush does nothing, and the
   * only clue is a toast that has already gone. The panel is where somebody
   * goes when a tool is not behaving, so the answer should be waiting there.
   */
  blockedReason?: () => string | null;
  /**
   * The two ways a stroke may leave the face it started on.
   *
   * Here rather than in the rail, which is a correction. `mirror-and-editor-
   * tools.md` put them there because "what they change is a stroke, which makes
   * them siblings of brush size and colour" — and brush size lives in THIS
   * panel, not beside it. They are also rare and need a sentence each to be
   * usable at all, and a popover has room for a sentence where a 32px icon does
   * not.
   *
   * Optional so the panel still mounts without them; the rows simply do not
   * appear, which is what happens below 1024px where there is no body to read a
   * fold from.
   */
  strokeReach?: {
    wrapFolds: () => boolean;
    setWrapFolds: (on: boolean) => void;
    crossParts: () => boolean;
    setCrossParts: (on: boolean) => void;
    /** False before a body has loaded — both settings are read off geometry. */
    available: () => boolean;
  };
}

/**
 * Fill's three behaviours, in the order they are reached for.
 *
 * "Face" and "Part" rather than "One face" and "Whole part", because those are
 * the same two words the face picker uses for the same two scopes. The editor
 * had the picker saying Face/Limb and Fill saying One face/Whole part — one
 * idea, two vocabularies, and a reader has to work out they mean the same
 * thing. The hint underneath carries the detail the longer labels were doing.
 */
const FILL_MODES: { id: "face" | "part" | "colour"; label: string; hint: string }[] = [
  {
    id: "face",
    label: "Face",
    hint: "Fills a single panel — the chest, or the front of a sleeve.",
  },
  {
    id: "part",
    label: "Part",
    hint: "Fills every side of the arm, leg or torso you tap. One undo takes it back.",
  },
  {
    id: "colour",
    label: "Matching colour",
    hint: "Spreads from where you tap, across pixels close to the colour under it.",
  },
];

/** Template pixels. 1 is a pixel-art line; 120 covers a face in a few strokes. */
const MIN_SIZE = 1;
const MAX_SIZE = 120;

export function mountToolOptions(opts: ToolOptionsHandlers) {
  const root = document.createElement("div");
  root.className = "opts popover floating";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "Tool options");

  const title = document.createElement("h3");
  title.className = "opts-title";

  /**
   * A dab at the real size.
   *
   * Size is chosen by looking, not by reading a number — 40px means nothing
   * until you see it against the canvas. Capped so a huge brush does not blow
   * the popover open; the readout carries the exact value.
   */
  const preview = document.createElement("div");
  preview.className = "opts-preview";
  const dab = document.createElement("i");
  preview.appendChild(dab);

  const sizeRow = row("Size", MIN_SIZE, MAX_SIZE, 1);
  const opacityRow = row("Opacity", 1, 100, 1);
  const hardnessRow = row("Hardness", 0, 100, 1);
  /*
    "Build-up", not "Flow".

    Flow is the industry word and it is opaque — it was read as a synonym for
    opacity, which is the one thing it is not. What it actually controls is how
    much paint a single pass leaves, so passing over an area again deepens it.
    Build-up says that, and the hint below says the rest.
  */
  const flowRow = row("Build-up", 1, 100, 1);
  /** One line, under the sliders, describing whichever brush is configured. */
  const hint = document.createElement("p");
  hint.className = "opts-hint";

  root.appendChild(title);
  root.appendChild(preview);
  root.appendChild(sizeRow.el);
  root.appendChild(opacityRow.el);
  root.appendChild(hardnessRow.el);
  root.appendChild(flowRow.el);
  root.appendChild(hint);

  /*
    Where a stroke may go, as two switches with their reasons attached.

    Only for the tools that make strokes. Fill never crosses a fold — it writes
    whole faces or a flood bounded to one — so showing it these would be a
    control that governs nothing, which is the defect this group has already
    produced twice.
  */
  const reach = document.createElement("div");
  reach.className = "opts-reach";
  const reachTitle = document.createElement("h4");
  reachTitle.className = "opts-subtitle";
  reachTitle.textContent = "Where a stroke can go";
  reach.appendChild(reachTitle);
  const wrapRow = switchRow(
    "Around folds",
    "Carry on around the limb onto the faces beside it. Off, a stroke stops at the edge of the face.",
  );
  const jointsRow = switchRow(
    "Across joints",
    "Carry onto another body part — arm to torso. Needs Around folds, because a joint is crossed by paint that already left its face.",
  );
  reach.appendChild(wrapRow.el);
  reach.appendChild(jointsRow.el);
  root.appendChild(reach);

  wrapRow.input.addEventListener("change", () => {
    opts.strokeReach?.setWrapFolds(wrapRow.input.checked);
    paint();
  });
  jointsRow.input.addEventListener("change", () => {
    opts.strokeReach?.setCrossParts(jointsRow.input.checked);
    paint();
  });
  document.body.appendChild(root);

  /*
    Fill's own controls.

    Built alongside the brush's and shown instead of them, because the panel
    belongs to whichever tool is active — and a fill has nothing to say about
    hardness while a brush has nothing to say about tolerance.
  */
  const fillModes = document.createElement("div");
  // Three now, so they wrap to their own rows rather than fighting for a half
  // each. Set as a data attribute so the CSS does not have to count children.
  fillModes.className = "opts-modes";
  fillModes.dataset.count = "3";
  const modeButtons = FILL_MODES.map((m) => {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "opts-mode";
    b.textContent = m.label;
    b.title = m.hint;
    b.addEventListener("click", () => {
      opts.tools.setFill({ mode: m.id });
      paint();
    });
    fillModes.appendChild(b);
    return { id: m.id, el: b, hint: m.hint };
  });
  const toleranceRow = row("Tolerance", 0, 96, 1);
  root.appendChild(fillModes);
  root.appendChild(toleranceRow.el);

  toleranceRow.input.addEventListener("input", () => {
    const v = Math.round(Number(toleranceRow.input.value));
    if (!Number.isFinite(v)) return;
    opts.tools.setFill({ tolerance: v });
    paint();
  });

  /** The button the panel is currently hanging from. */
  let anchor: HTMLElement = opts.anchor;

  /** A labelled checkbox with the sentence that makes it usable underneath. */
  function switchRow(label: string, why: string) {
    const el = document.createElement("label");
    el.className = "opts-switch";
    const input = document.createElement("input");
    input.type = "checkbox";
    const text = document.createElement("span");
    const name = document.createElement("span");
    name.className = "opts-switch-name";
    name.textContent = label;
    const note = document.createElement("small");
    note.className = "opts-switch-why";
    note.textContent = why;
    text.appendChild(name);
    text.appendChild(note);
    el.appendChild(input);
    el.appendChild(text);
    return { el, input, note };
  }

  function row(label: string, min: number, max: number, step: number) {
    const el = document.createElement("label");
    el.className = "opts-row";
    const name = document.createElement("span");
    name.textContent = label;
    const value = document.createElement("span");
    value.className = "opts-value";
    const input = document.createElement("input");
    input.type = "range";
    input.min = String(min);
    input.max = String(max);
    input.step = String(step);
    // A range input is keyboard-operable for free, which a custom drag handle
    // would have to reimplement — and it gets the 44px touch target from CSS.
    el.appendChild(name);
    el.appendChild(value);
    el.appendChild(input);
    return { el, input, value };
  }

  /**
   * Re-render, then put the panel back where it fits.
   *
   * Placing *after* painting is what makes hiding a row safe. `attachPopover`
   * places once on open and never again — and it does so before `onOpen`, so
   * the panel was positioned while it still held the previous tool's controls.
   * Measuring after the content is final fixes that, and it means a row can now
   * come and go without the panel hanging off the bottom of the window.
   */
  function paint() {
    paintContents();
    if (popover.isOpen) placePanel();
  }

  function paintContents() {
    const state = opts.tools.get();
    const { size, opacity } = state.brush;
    const filling = state.active === "fill";

    /*
      Strokes only. Fill writes whole faces or a bounded flood, so it never
      crosses a fold and these would govern nothing for it.
    */
    const reachable = Boolean(opts.strokeReach) && !filling;
    reach.hidden = !reachable;
    if (reachable && opts.strokeReach) {
      const ready = opts.strokeReach.available();
      const wrapping = opts.strokeReach.wrapFolds();
      wrapRow.input.checked = wrapping;
      wrapRow.input.disabled = !ready;
      // Joints is meaningless until something has left its own face, and
      // saying so beats a switch that flips and does nothing.
      jointsRow.input.checked = opts.strokeReach.crossParts();
      jointsRow.input.disabled = !ready || !wrapping;
      jointsRow.el.dataset.inert = String(!ready || !wrapping);
      wrapRow.el.dataset.inert = String(!ready);
      if (!ready) {
        wrapRow.note.textContent = "Load a body first — a fold is read off its geometry.";
      } else {
        wrapRow.note.textContent =
          "Carry on around the limb onto the faces beside it. Off, a stroke stops at the edge of the face.";
      }
    }

    title.textContent = filling ? "Fill" : state.active === "eraser" ? "Eraser" : "Brush";

    /*
      One panel, two tools' worth of controls, and only ever one shown.

      Hiding rather than building a second popover keeps the open/close
      behaviour, the anchoring and the dismissal in one place — three things
      that were each subtly wrong at least once already.
    */
    /*
      Read once, and it outranks whatever the tool would otherwise say. A hint
      explaining how Build-up works is noise when the reason nothing happens is
      that the selected layer is an image.
    */
    const blocked = opts.blockedReason?.() ?? null;
    const toleranceLive = filling && state.fill.mode === "colour";
    const flowLive = !filling && state.brush.hardness < 1;

    preview.hidden = filling;
    sizeRow.el.hidden = filling;
    opacityRow.el.hidden = filling;
    hardnessRow.el.hidden = filling;
    fillModes.hidden = !filling;
    /*
      Shown only where they do something.

      Both of these used to sit there disabled, reading "—", with a title
      explaining the absence and an `inert` flag styling it — four mechanisms
      to describe a control that does nothing. Tolerance applies to one fill
      mode of three; Build-up needs a soft edge. The stated reason for keeping
      them was that removing them would make the panel jump height, which only
      held because the panel was never re-placed after it changed. It is now.

      The hint underneath already says how to bring each one back, so nothing
      is lost by letting the row go.
    */
    flowRow.el.hidden = !flowLive;
    toleranceRow.el.hidden = !toleranceLive;

    if (filling) {
      const { mode, tolerance } = state.fill;
      for (const b of modeButtons) {
        b.el.dataset.selected = String(b.id === mode);
        b.el.setAttribute("aria-pressed", String(b.id === mode));
      }
      if (toleranceLive) {
        // The slider itself was never synced from state — only its readout —
        // so it sat wherever it was last dragged.
        toleranceRow.input.value = String(tolerance);
        toleranceRow.value.textContent = String(tolerance);
      }
      hint.textContent = blocked ?? modeButtons.find((b) => b.id === mode)?.hint ?? "";
      hint.dataset.blocked = String(Boolean(blocked));
      return;
    }

    const { hardness, flow } = state.brush;
    sizeRow.input.value = String(size);
    sizeRow.value.textContent = `${size} px`;
    opacityRow.input.value = String(Math.round(opacity * 100));
    opacityRow.value.textContent = `${Math.round(opacity * 100)}%`;

    hardnessRow.input.value = String(Math.round(hardness * 100));
    hardnessRow.value.textContent =
      hardness >= 1 ? "Hard" : hardness <= 0 ? "Softest" : `${Math.round(hardness * 100)}%`;

    /*
      At hardness 1 the painter fills a single outline — there are no stamps,
      so there is nothing to accumulate and the row is not shown. The hint
      below is what says how to get it back.
    */
    flowRow.input.value = String(Math.round(flow * 100));
    flowRow.value.textContent = `${Math.round(flow * 100)}%`;
    flowRow.el.title = "How much paint one pass leaves";
    hint.textContent =
      blocked ??
      (flowLive
        ? "Each pass leaves this much paint. Go over an area again to deepen it."
        : "A hard edge lays down one even pass. Lower Hardness to build up shading.");
    hint.dataset.blocked = String(Boolean(blocked));

    const shown = Math.min(size, 64);
    dab.style.inlineSize = `${shown}px`;
    dab.style.blockSize = `${shown}px`;
    dab.style.opacity = String(opacity);
    // The dab shows the edge it will actually leave, so hardness is chosen by
    // looking rather than by reading a percentage.
    dab.style.filter = hardness >= 1 ? "none" : `blur(${((1 - hardness) * shown) / 6}px)`;
    // The eraser previews as an outline: a filled dab in the brush colour would
    // say the opposite of what the tool does.
    if (state.active === "eraser") dab.setAttribute("data-erasing", "");
    else dab.removeAttribute("data-erasing");
  }

  sizeRow.input.addEventListener("input", () => {
    const px = Math.round(Number(sizeRow.input.value));
    if (!Number.isFinite(px)) return;
    opts.tools.setBrush({ size: px });
    opts.onSize(px);
  });

  opacityRow.input.addEventListener("input", () => {
    const pct = Math.round(Number(opacityRow.input.value));
    if (!Number.isFinite(pct)) return;
    const fraction = Math.min(1, Math.max(0, pct / 100));
    opts.tools.setBrush({ opacity: fraction });
    opts.onOpacity(fraction);
  });

  hardnessRow.input.addEventListener("input", () => {
    const pct = Math.round(Number(hardnessRow.input.value));
    if (!Number.isFinite(pct)) return;
    const value = Math.min(1, Math.max(0, pct / 100));
    opts.tools.setBrush({ hardness: value });
    opts.onHardness(value);
    paint();
  });

  flowRow.input.addEventListener("input", () => {
    const pct = Math.round(Number(flowRow.input.value));
    if (!Number.isFinite(pct)) return;
    const value = Math.min(1, Math.max(0, pct / 100));
    opts.tools.setBrush({ flow: value });
    opts.onFlow(value);
    paint();
  });

  /*
    A detached anchor, on purpose.

    attachPopover binds its own click-toggle to whatever anchor it is given, and
    the anchor here would be a tool button that already has a click handler
    selecting the tool. Both would fire on one click and the panel would toggle
    twice — open then shut, reading as a popover that never opens at all. The
    same trap main.ts already documents for the colour swatch.

    So the popover hangs from a node nobody can click, `openAt` decides where it
    is drawn, and the aria-expanded state is put on the real button by hand.
  */
  const ghost = document.createElement("span");
  let expandedOn: HTMLElement | null = null;

  /** Beside the anchor, and never off the window. */
  function placePanel() {
    const r = anchor.getBoundingClientRect();
    const own = root.getBoundingClientRect();
    const left = Math.min(r.right + 8, window.innerWidth - own.width - 8);
    const top = Math.min(Math.max(8, r.top), window.innerHeight - own.height - 8);
    root.style.left = `${Math.max(8, left)}px`;
    root.style.top = `${Math.max(8, top)}px`;
  }

  const popover = attachPopover({
    anchor: ghost,
    panel: root,
    // The button it is open from is not the anchor, so dismissal has to be told
    // about it — otherwise pressing that button closes the panel on pointerdown
    // and its own handler reopens it on click, and it never shuts.
    alsoInside: () => expandedOn,
    place: placePanel,
    onOpen: paint,
    onClose() {
      expandedOn?.setAttribute("aria-expanded", "false");
      expandedOn = null;
    },
  });

  /*
    Subscribed AFTER the popover exists, and that ordering is load-bearing.

    `subscribe` calls its listener immediately — it hands over the current state
    so a fresh subscriber is never a frame behind. `paint` reads
    `popover.isOpen` to decide whether to re-place the panel, so subscribing
    before the `const popover` above threw
    `Cannot access 'popover' before initialization` out of `wireTools`, which
    took the whole editor down with it: no viewer, no body, no mirror controls,
    and a canvas still asking for a template PNG.

    A `const` in a temporal dead zone rather than an undefined, so it failed
    loudly — but the failure surfaced as "editor failed to start" a long way
    from this line.
  */
  opts.tools.subscribe(paint);

  return {
    ...popover,
    /**
     * Re-render without opening.
     *
     * The panel subscribes to the TOOL, so it repaints when the tool changes
     * and at no other time — but what it shows also depends on the selected
     * LAYER now that it reports why a stroke would go nowhere. Selecting an
     * image left the hint describing the brush that image had just disabled.
     */
    refresh: paint,
    /**
     * Show the panel against a specific tool button.
     *
     * The options belong to the tool, so they open from whichever tool button
     * was clicked rather than from a settings button sitting next to it. There
     * is no separate settings control any more: a second click on the active
     * tool is what opens this.
     */
    openAt(el: HTMLElement) {
      /*
        A second press on the button it is already open from closes it.

        Without this the panel could only be dismissed by clicking away, so
        pressing the tool again — the obvious way to put it back — reopened it
        and it looked stuck. `openAt` is called from the tool's own click
        handler, so this is the toggle a user expects from any disclosure.
      */
      if (popover.isOpen && expandedOn === el) {
        popover.close();
        return;
      }
      anchor = el;
      expandedOn?.setAttribute("aria-expanded", "false");
      expandedOn = el;
      // The tool button keeps aria-pressed for "this is the active tool"; this
      // is the separate claim that a panel is open from it.
      el.setAttribute("aria-expanded", "true");
      el.setAttribute("aria-haspopup", "dialog");
      popover.open();
    },
  };
}
